import { Link } from 'react-router-dom'

import { contactCopy } from '../content/contact'
import { SUPPORT_EMAIL } from '../content/site'
import { PAGES, type Lang } from '../i18n'
import styles from './ContactPage.module.css'

/**
 * One address, three lists and the company behind the app.
 *
 * The e-mail is a real mailto link and the only interactive thing on the page: there is
 * no form, because a form needs a backend and Ronin's whole claim is that there is not
 * one. See the note at the top of content/contact.ts.
 *
 * Reading width matches the legal pages (container--reading) so the three pages of the
 * site that are prose rather than layout look like one another.
 */
export default function ContactPage({ lang }: { lang: Lang }) {
  const t = contactCopy[lang]

  return (
    <article className={`container--reading ${styles.article}`}>
      <Link to={PAGES.home[lang]} className={styles.back}>
        {lang === 'sv' ? 'Tillbaka till Ronin' : 'Back to Ronin'}
      </Link>

      <h1 className={styles.title}>{t.pageTitle}</h1>
      <p className={styles.lede}>{t.lede}</p>

      <section className={styles.mail}>
        <h2 className={styles.mailTitle}>{t.emailTitle}</h2>
        <p className={styles.body}>{t.emailBody}</p>
        <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.address}>
          {SUPPORT_EMAIL}
        </a>
        <p className={styles.note}>{t.emailNote}</p>
      </section>

      {/* Ronin is not a care provider. This sits high on the page on purpose. */}
      <section className={styles.care}>
        <h2 className={styles.sectionTitle}>{t.careTitle}</h2>
        <p className={styles.body}>{t.careBody}</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t.writeTitle}</h2>
        <p className={styles.body}>{t.writeBody}</p>
        <ul className={styles.points}>
          {t.writePoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t.selfTitle}</h2>
        <p className={styles.body}>{t.selfBody}</p>
        <ul className={styles.points}>
          {t.selfPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t.legalTitle}</h2>
        <p className={styles.body}>{t.legalBody}</p>
        <p className={styles.links}>
          <Link to={PAGES.privacy[lang]}>{t.privacyLinkLabel}</Link>
          <Link to={PAGES.terms[lang]}>{t.termsLinkLabel}</Link>
        </p>
      </section>

      <div className={styles.org}>
        <h2 className={styles.orgTitle}>{t.orgTitle}</h2>
        {t.orgLines.map((line) => (
          <p key={line} className={styles.orgLine}>
            {line}
          </p>
        ))}
        <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.orgLink}>
          {SUPPORT_EMAIL}
        </a>
      </div>
    </article>
  )
}
